import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ArrowClockwise, Code, Play, Plugs, SignOut, X
} from '@phosphor-icons/react';
import ContextHelpButton from '../ContextHelpButton';

const STATUS_STYLES = {
  connected: { color: '#4ade80', background: 'rgba(74,222,128,0.10)' },
  available: { color: '#67e8f9', background: 'rgba(103,232,249,0.10)' },
  setup_required: { color: '#fbbf24', background: 'rgba(251,191,36,0.10)' },
  planned: { color: '#a78bfa', background: 'rgba(167,139,250,0.10)' },
  unavailable: { color: '#94a3b8', background: 'rgba(148,163,184,0.10)' },
};

const ALWAYS_OPERATIONAL_CONNECTORS = new Set(['openai_visual', 'canva']);
const CONDITIONAL_OPERATIONAL_CONNECTORS = new Set(['magnific']);

function isOperationalConnector(connector) {
  if (ALWAYS_OPERATIONAL_CONNECTORS.has(connector.id)) return true;
  if (CONDITIONAL_OPERATIONAL_CONNECTORS.has(connector.id)) {
    return connector.status === 'connected';
  }
  return false;
}

function toolArgumentsExample(schema = {}) {
  const properties = schema.properties || {};
  return Object.fromEntries(Object.entries(properties).map(([key, definition]) => {
    if (definition.default !== undefined) return [key, definition.default];
    if (definition.examples?.length) return [key, definition.examples[0]];
    if (definition.type === 'boolean') return [key, false];
    if (definition.type === 'number' || definition.type === 'integer') return [key, 0];
    if (definition.type === 'array') return [key, []];
    if (definition.type === 'object') return [key, {}];
    return [key, ''];
  }));
}

// PRODUCT-IA-CLEANUP-001A: extracted from the historical CreativeStudioTab
// — this is the Creative Tools contextual group inside the Connections
// workspace. Owns connector connect/disconnect and the MCP tool
// workbench only; moodboard/asset management moved to MoodboardSection
// (now under DNA / Moodboard) and no longer lives here.
export default function CreativeToolsPanel({ api, project }) {
  const { t } = useTranslation();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectorBusy, setConnectorBusy] = useState(null);
  const [workbench, setWorkbench] = useState(null);
  const [selectedToolName, setSelectedToolName] = useState('');
  const [toolArguments, setToolArguments] = useState('{}');
  const [executingTool, setExecutingTool] = useState(false);
  const [toolResult, setToolResult] = useState(null);

  const connectorSections = useMemo(() => {
    const operational = [];
    const experimental = [];

    (connectors || []).forEach((connector) => {
      if (isOperationalConnector(connector)) {
        operational.push(connector);
      } else {
        experimental.push(connector);
      }
    });

    return { operational, experimental };
  }, [connectors]);

  const loadConnectors = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${project.id}/creative-studio`);
      setConnectors(data.connectors || []);
    } catch (e) {
      toast.error(t('project.creative.loadError', { message: e.response?.data?.detail || e.message }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (project?.id) loadConnectors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  useEffect(() => {
    const handleConnectorMessage = async (event) => {
      if (event.data?.type === 'mcp_connector_success') {
        toast.success(t('project.creative.connectorConnected'));
        setConnectorBusy(null);
        await loadConnectors();
      }
      if (event.data?.type === 'mcp_connector_error') {
        const message = event.data.error === 'provider_grant_failed'
          ? t('project.creative.providerGrantFailed')
          : event.data.error || 'OAuth';
        toast.error(t('project.creative.connectorError', { message }));
        setConnectorBusy(null);
      }
    };
    window.addEventListener('message', handleConnectorMessage);
    return () => window.removeEventListener('message', handleConnectorMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, t]);

  const connectConnector = async (connector) => {
    setConnectorBusy(connector.id);
    try {
      const { data } = await api.get(`/projects/${project.id}/creative-studio/connectors/${connector.id}/connect`);
      const popup = window.open(data.auth_url, `mcp-${connector.id}`, 'width=620,height=760,resizable=yes,scrollbars=yes');
      if (!popup) {
        setConnectorBusy(null);
        toast.error(t('project.creative.popupBlocked'));
      }
    } catch (e) {
      setConnectorBusy(null);
      toast.error(t('project.creative.connectorError', { message: e.response?.data?.detail || e.message }));
    }
  };

  const openConnectorWorkbench = async (connector) => {
    setConnectorBusy(connector.id);
    try {
      const { data } = await api.post(`/projects/${project.id}/creative-studio/connectors/${connector.id}/discover`);
      const tools = data.tools || [];
      setWorkbench({ ...connector, tools });
      setToolResult(null);
      const firstTool = tools[0];
      setSelectedToolName(firstTool?.name || '');
      setToolArguments(JSON.stringify(toolArgumentsExample(firstTool?.inputSchema), null, 2));
    } catch (e) {
      toast.error(t('project.creative.discoveryError', { message: e.response?.data?.detail || e.message }));
    } finally {
      setConnectorBusy(null);
    }
  };

  const selectWorkbenchTool = (toolName) => {
    const tool = workbench?.tools?.find(item => item.name === toolName);
    setSelectedToolName(toolName);
    setToolArguments(JSON.stringify(toolArgumentsExample(tool?.inputSchema), null, 2));
    setToolResult(null);
  };

  const executeWorkbenchTool = async () => {
    let parsedArguments;
    try {
      parsedArguments = JSON.parse(toolArguments || '{}');
    } catch {
      toast.error(t('project.creative.invalidJson'));
      return;
    }
    setExecutingTool(true);
    setToolResult(null);
    try {
      const { data } = await api.post(
        `/projects/${project.id}/creative-studio/connectors/${workbench.id}/execute`,
        { tool_name: selectedToolName, arguments: parsedArguments, import_images: true }
      );
      setToolResult(data.result || {});
      if (data.assets?.length) {
        toast.success(t('project.creative.toolAssetsImported', { count: data.assets.length }));
      } else {
        toast.success(t('project.creative.toolCompleted'));
      }
    } catch (e) {
      toast.error(t('project.creative.toolError', { message: e.response?.data?.detail || e.message }));
    } finally {
      setExecutingTool(false);
    }
  };

  const disconnectConnector = async (connector) => {
    setConnectorBusy(connector.id);
    try {
      await api.delete(`/projects/${project.id}/creative-studio/connectors/${connector.id}`);
      if (workbench?.id === connector.id) setWorkbench(null);
      await loadConnectors();
      toast.success(t('project.creative.connectorDisconnected'));
    } catch (e) {
      toast.error(t('project.creative.connectorError', { message: e.response?.data?.detail || e.message }));
    } finally {
      setConnectorBusy(null);
    }
  };

  if (loading) {
    return <p className="text-sm text-[var(--text-muted)] py-8">{t('common.loading')}</p>;
  }

  const renderConnectorCard = (connector, isExperimental = false) => {
    const statusStyle = STATUS_STYLES[connector.status] || STATUS_STYLES.unavailable;
    const descriptionKey = `project.creative.connectorDescriptions.${connector.id}`;
    const translatedDescription = t(descriptionKey);
    const connectorDescription = translatedDescription === descriptionKey
      ? connector.capabilities.map(item => t(`project.creative.capabilities.${item}`)).join(' · ')
      : translatedDescription;

    return (
      <div
        key={connector.id}
        className={`rounded-2xl border p-3 ${isExperimental ? 'bg-white/[0.015] opacity-80' : 'bg-white/[0.025]'}`}
        style={{ borderColor: 'var(--border-color)' }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium">{connector.name}</p>
            <p className="text-[10px] text-[var(--text-muted)] mt-1">{connectorDescription}</p>
          </div>
          <span className="rounded-full px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wide" style={statusStyle}>
            {t(`project.creative.status.${connector.status}`)}
          </span>
        </div>
        {connector.can_connect && connector.status === 'available' && (
          <button
            className="btn-gradient text-[11px] w-full mt-3"
            disabled={connectorBusy === connector.id}
            onClick={() => connectConnector(connector)}
          >
            <Plugs size={14} />
            {connectorBusy === connector.id ? t('project.creative.connecting') : t('project.creative.connect')}
          </button>
        )}
        {connector.status === 'setup_required' && connector.setup_hint && (
          <p className="mt-3 rounded-xl border border-amber-400/20 bg-amber-400/5 px-3 py-2 text-[10px] leading-relaxed text-amber-200">
            {t('project.creative.connectorSetupHint', { variable: connector.setup_hint })}
          </p>
        )}
        {connector.provider_notice && (
          <p className="mt-3 rounded-xl border border-cyan-300/15 bg-cyan-300/[0.035] px-3 py-2 text-[10px] leading-relaxed text-cyan-100/80">
            {t(`project.creative.providerNotices.${connector.provider_notice}`)}
            {connector.provider_action_url && (
              <>
                {' '}
                <a
                  href={connector.provider_action_url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-cyan-200 underline underline-offset-2"
                >
                  {t('project.creative.providerApprovalLink')}
                </a>
              </>
            )}
          </p>
        )}
        {connector.can_execute && connector.status === 'connected' && (
          <div className="flex gap-2 mt-3">
            <button
              className="btn-gradient text-[11px] flex-1"
              disabled={connectorBusy === connector.id}
              onClick={() => openConnectorWorkbench(connector)}
            >
              <Code size={14} />
              {connectorBusy === connector.id ? t('project.creative.discovering') : t('project.creative.openTools')}
            </button>
            {connector.can_connect && (
              <button
                className="btn-ghost p-2.5"
                title={t('project.creative.disconnect')}
                onClick={() => disconnectConnector(connector)}
              >
                <SignOut size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="pt-2 pb-12 space-y-5">
      <section className="card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><Plugs size={19} className="text-cyan-300" /> {t('project.creative.connectorsTitle')}</h3>
            <p className="text-xs text-[var(--text-muted)] mt-1">{t('project.creative.connectorsHint')}</p>
          </div>
          <ContextHelpButton articleId="creative-tools" label="Guida Creative Tools" />
        </div>
        <div className="space-y-4 mt-4">
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-300">
                {t('project.creative.sections.operationalTitle')}
              </h4>
            </div>
            <p className="text-[10px] text-[var(--text-muted)] mb-3">
              {t('project.creative.sections.operationalHint')}
            </p>
            <div className="space-y-2">
              {connectorSections.operational.map((connector) => renderConnectorCard(connector))}
            </div>
          </div>

          {connectorSections.experimental.length > 0 && (
            <div className="pt-3 border-t border-[var(--border-color)]">
              <div className="flex items-center justify-between gap-3 mb-2">
                <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-violet-300">
                  {t('project.creative.sections.experimentalTitle')}
                </h4>
              </div>
              <p className="text-[10px] text-[var(--text-muted)] mb-3">
                {t('project.creative.sections.experimentalHint')}
              </p>
              <div className="space-y-2">
                {connectorSections.experimental.map((connector) => renderConnectorCard(connector, true))}
              </div>
            </div>
          )}
        </div>
        <p className="text-[10px] text-[var(--text-muted)] mt-4 leading-relaxed">{t('project.creative.connectorSafety')}</p>
      </section>

      {workbench && (
        <div className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={event => { if (event.target === event.currentTarget) setWorkbench(null); }}>
          <div className="w-full max-w-3xl max-h-[90vh] rounded-[28px] border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden flex flex-col">
            <div className="p-4 md:p-5 border-b border-[var(--border-color)] flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-300">{t('project.creative.mcpWorkbench')}</p>
                <h3 className="font-semibold mt-1">{workbench.name}</h3>
                <p className="text-xs text-[var(--text-muted)] mt-1">{t('project.creative.mcpWorkbenchHint')}</p>
              </div>
              <button className="btn-ghost p-2" onClick={() => setWorkbench(null)}><X size={16} /></button>
            </div>
            <div className="p-4 md:p-5 overflow-y-auto space-y-4">
              {workbench.tools.length === 0 ? (
                <div className="py-12 text-center">
                  <Code size={30} className="mx-auto text-[var(--text-muted)] mb-3" />
                  <p className="text-sm text-[var(--text-muted)]">{t('project.creative.noTools')}</p>
                </div>
              ) : (
                <>
                  <label className="block">
                    <span className="text-xs text-[var(--text-muted)]">{t('project.creative.chooseTool')}</span>
                    <select className="input-dark w-full mt-2 p-3" value={selectedToolName} onChange={event => selectWorkbenchTool(event.target.value)}>
                      {workbench.tools.map(tool => <option key={tool.name} value={tool.name}>{tool.name}</option>)}
                    </select>
                  </label>
                  <div className="rounded-2xl border border-cyan-300/15 bg-cyan-300/[0.035] p-3">
                    <p className="text-xs leading-relaxed text-[var(--text-muted)]">
                      {workbench.tools.find(tool => tool.name === selectedToolName)?.description || t('project.creative.noToolDescription')}
                    </p>
                  </div>
                  <label className="block">
                    <span className="text-xs text-[var(--text-muted)]">{t('project.creative.toolArguments')}</span>
                    <textarea
                      className="input-dark font-mono text-xs min-h-56 w-full mt-2 p-3"
                      value={toolArguments}
                      onChange={event => setToolArguments(event.target.value)}
                      spellCheck={false}
                    />
                  </label>
                  {toolResult && (
                    <div>
                      <p className="text-xs text-[var(--text-muted)] mb-2">{t('project.creative.toolResult')}</p>
                      <pre className="rounded-2xl border border-[var(--border-color)] bg-black/30 p-3 text-[11px] whitespace-pre-wrap break-words max-h-56 overflow-auto">
                        {JSON.stringify(toolResult, null, 2)}
                      </pre>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="p-4 border-t border-[var(--border-color)] flex items-center justify-between gap-3">
              <button className="btn-ghost text-xs" onClick={() => openConnectorWorkbench(workbench)} disabled={connectorBusy === workbench.id}>
                <ArrowClockwise size={14} /> {t('project.creative.refreshTools')}
              </button>
              <button className="btn-gradient text-xs" onClick={executeWorkbenchTool} disabled={!selectedToolName || executingTool}>
                <Play size={14} /> {executingTool ? t('project.creative.executingTool') : t('project.creative.executeTool')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
